import React, { useRef, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import TopologySidebar from "./TopologySidebar";
import { useNavigate } from "react-router-dom";
import AddGroup from "../../../components/addGroup/AddGroup";
import { UPDATE_SIDEBAR_WIDTH } from "../../../constants/topologyConstants";

/**
 * TopologySidebarWrapper component with a draggable resizer.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {React.ReactNode} props.children - The child components to be rendered inside the wrapper.
 * @param {number} props.sidebarWidth - The width of the sidebar.
 * @return {JSX.Element} The rendered component.
 */
export function TopologySidebarWrapper({ children, sidebarWidth: propSidebarWidth }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const topology = useSelector((state) => state.topology);
  const { sideBarTopologyOpen } = topology;
  const sidebarWidth = propSidebarWidth || topology.sidebarWidth;
  const announcementBannerClose = useSelector(
    (state) => state.announcementBannerCloseTrigger.announcementBannerClose
  );
  const maintenanceInstances = useSelector(
    (state) => state.maintenanceInstanceList.maintenanceInstances
  );
  const msg = maintenanceInstances?.data?.notification_msg;
  const dynamicTop = (msg && !announcementBannerClose) ? '32px' : '0px';

  const containerRef = useRef(null);
  const sidebarRef = useRef(null);
  const contentRef = useRef(null);
  const initialWidthRef = useRef(null);
  const initialMouseXRef = useRef(null);

  const handleMouseMove = useCallback((e) => {
    if (containerRef.current && initialWidthRef.current !== null && initialMouseXRef.current !== null) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const deltaX = e.clientX - initialMouseXRef.current;
      const newWidth = initialWidthRef.current + deltaX;
      const newValidWidth = Math.max(239, Math.min(containerRect.width - 840, newWidth));

      if (sidebarRef.current) {
        sidebarRef.current.style.width = `${newValidWidth}px`;
      }
      // Update the sidebar width in real-time for smooth movement
      dispatch({ type: UPDATE_SIDEBAR_WIDTH, payload: newValidWidth });
    }
  }, [dispatch]);

  const handleMouseUp = useCallback(() => {
    const finalWidth = sidebarRef.current.getBoundingClientRect().width;
    dispatch({ type: UPDATE_SIDEBAR_WIDTH, payload: finalWidth });

    // Reset initial values
    initialWidthRef.current = null;
    initialMouseXRef.current = null;

    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  }, [handleMouseMove, dispatch]);

  const handleMouseDown = useCallback((e) => {
    if (sidebarRef.current) {
      initialWidthRef.current = sidebarRef.current.getBoundingClientRect().width;
      initialMouseXRef.current = e.clientX;
    }
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  }, [handleMouseMove, handleMouseUp]);

  // Cleanup event listeners on component unmount
  useEffect(() => {
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);


  return (
    <div
      ref={containerRef}
      style={{
        height: "100%",
        outline: "none",
        display: "flex",
        flexDirection: "row",
        // We'll calculate the width of children using this value.
        position: 'relative',
        width: '100%',
      }}
    >
      {sideBarTopologyOpen && (
        <>
          <div
            ref={sidebarRef}
            style={{
              height: "100%",
              width: `${sidebarWidth}px`,
              flexShrink: 0,
              transition: 'none', // Remove transition during drag for smoothness
            }}
          >
            <TopologySidebar
              setIsHovered={() => null}
              onTopologySelected={() => {
                navigate("/topology");
              }}
              sidebarWidth={sidebarWidth}
            />
          </div>
          {/* Draggable Resizer Bar */}
          <div
            onMouseDown={handleMouseDown}
            className="draggable-resizer"
            style={{
              height: "100vh",
              // To ensure it's on top of other content
              zIndex: 25,
              userSelect: "none", // Prevent text selection during drag
              cursor: 'ew-resize', // Change cursor for better user experience
              width: '1px',
              position: 'fixed',
              left: `${sidebarWidth + 58}px`,
              top: dynamicTop,
            }}
          />
        </>
      )}
      <div
        ref={contentRef}
        style={{
          height: "100%",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          // Set a minimum width to prevent it from disappearing
          minWidth: sideBarTopologyOpen ? `calc(100% - ${sidebarWidth}px)` : '100%',
          outline: "none",
        }}
      >
        {children}
      </div>
      {<AddGroup onAdd={() => null} />}
    </div>
  );
}

TopologySidebarWrapper.propTypes = {
  children: PropTypes.node.isRequired,
  sidebarWidth: PropTypes.number,
};
